import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, CalendarCheck, PartyPopper, Eye, Pencil, Users, X, Check, XCircle, Clock,
  AlertTriangle, CalendarDays, Sparkles, RefreshCcw,
} from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { moduleCanDo, tabAccessibleFor } from "../lib/modules";

const STATUS_TONES = {
  approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200 border-emerald-200 dark:border-emerald-500/40",
  pending:  "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 border-amber-200 dark:border-amber-500/40",
  rejected: "bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200 border-rose-200 dark:border-rose-500/40",
};

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s + "T00:00:00");
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
};
const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return s; }
};

export default function Holidays() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const canCreate = isMaster || tabAccessibleFor(user, "holidays", "create") || moduleCanDo(user, "holidays", "edit");
  const [viewMode] = useGlobalViewMode();

  const [year, setYear] = useState(new Date().getFullYear());
  const [approved, setApproved] = useState([]);
  const [all, setAll] = useState([]);
  const [nextHol, setNextHol] = useState(null);
  const [tab, setTab] = useState("view");

  const [openForm, setOpenForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null); // for edit-request flow
  const [deletingHoliday, setDeletingHoliday] = useState(null); // for delete-request flow

  const loadApproved = useCallback(async () => {
    try {
      const { data } = await api.get(`/holidays?year=${year}&status=approved`);
      setApproved(data || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Load failed"); }
  }, [year]);
  const loadAll = useCallback(async () => {
    if (!canCreate) return;
    try {
      const { data } = await api.get(`/holidays?year=${year}&status=all`);
      setAll(data || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Load failed"); }
  }, [year, canCreate]);
  const loadNext = useCallback(async () => {
    try {
      const { data } = await api.get("/holidays/next");
      setNextHol(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadApproved(); }, [loadApproved]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadNext(); }, [loadNext]);

  const refreshAll = () => { loadApproved(); loadAll(); loadNext(); };

  return (
    <div className="max-w-[1400px] space-y-6">
      <PageHeader
        moduleKey="holidays"
        tone="emerald"
        icon={PartyPopper}
        eyebrow="HR Module"
        title="Holidays"
        subtitle="See approved holidays and submit new ones for Master Admin approval."
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1 bg-slate-100 dark:bg-slate-900/60 p-1 rounded-lg" data-testid="holidays-tabs">
          <TabsTrigger value="view" data-testid="holidays-tab-view" className="flex items-center gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Eye className="w-3.5 h-3.5" /> View
          </TabsTrigger>
          {canCreate && (
            <TabsTrigger value="create" data-testid="holidays-tab-create" className="flex items-center gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Pencil className="w-3.5 h-3.5" /> Create
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="view" className="mt-4 space-y-4" data-testid="holidays-view-content">
          <NextHolidayCard next={nextHol} />
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-[11.5px] uppercase tracking-wider font-semibold text-slate-600">Year</Label>
            <YearPicker value={year} onChange={setYear} testid="holidays-view-year" />
            <div className="flex-1" />
            <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">{approved.length} approved</Badge>
            <Button size="sm" variant="outline" onClick={refreshAll} className="h-9" data-testid="holidays-view-refresh">
              <RefreshCcw size={14} className="mr-1.5" /> Refresh
            </Button>
          </div>
          <HolidaysView
            mode={viewMode}
            rows={approved}
            variant="approved"
            emptyMsg={`No approved holidays for ${year} yet.`}
            testidPrefix="holidays-view"
          />
        </TabsContent>

        {canCreate && (
          <TabsContent value="create" className="mt-4 space-y-4" data-testid="holidays-create-content">
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-[11.5px] uppercase tracking-wider font-semibold text-slate-600">Year</Label>
              <YearPicker value={year} onChange={setYear} testid="holidays-create-year" />
              <div className="flex-1" />
              <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">{all.length} total</Badge>
              <Button size="sm" variant="outline" onClick={refreshAll} className="h-9" data-testid="holidays-create-refresh">
                <RefreshCcw size={14} className="mr-1.5" /> Refresh
              </Button>
              <Button size="sm" onClick={() => setOpenForm(true)} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="holidays-create-add">
                <Plus size={14} className="mr-1.5" /> Add Holiday
              </Button>
            </div>
            <HolidaysView
              mode={viewMode}
              rows={all}
              variant="create"
              isMaster={isMaster}
              currentUserId={user?.id}
              onEditRequest={setEditingHoliday}
              onDeleteRequest={setDeletingHoliday}
              onApprove={async (h, e) => {
                try { await api.post(`/holidays/${h.id}/approve`, {}); toast.success(`Approved: ${h.name}`); refreshAll(); }
                catch (err) { toast.error(formatError(err.response?.data?.detail)); }
                if (e) e.stopPropagation();
              }}
              onReject={async (h, reason) => {
                try { await api.post(`/holidays/${h.id}/reject`, { reason }); toast.success(`Rejected: ${h.name}`); refreshAll(); }
                catch (err) { toast.error(formatError(err.response?.data?.detail)); }
              }}
              emptyMsg={`No holidays submitted for ${year} yet. Click "Add Holiday" to start.`}
              testidPrefix="holidays-create"
            />
          </TabsContent>
        )}
      </Tabs>

      {openForm && (
        <AddHolidaysDialog
          open={openForm}
          onClose={() => setOpenForm(false)}
          onSaved={() => { setOpenForm(false); refreshAll(); }}
          isMaster={isMaster}
        />
      )}
      {editingHoliday && (
        <EditRequestDialog
          open={!!editingHoliday}
          holiday={editingHoliday}
          onClose={() => setEditingHoliday(null)}
          onSaved={() => { setEditingHoliday(null); refreshAll(); }}
        />
      )}
      {deletingHoliday && (
        <DeleteRequestDialog
          open={!!deletingHoliday}
          holiday={deletingHoliday}
          onClose={() => setDeletingHoliday(null)}
          onSaved={() => { setDeletingHoliday(null); refreshAll(); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Next Holiday countdown
// ────────────────────────────────────────────────────────────────
function NextHolidayCard({ next }) {
  const h = next?.holiday;
  const days = next?.days_away;
  if (!h) {
    return (
      <div className="pk-card p-5 border-l-4 border-slate-300 dark:border-slate-700" data-testid="holidays-next-empty">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center">
            <CalendarDays className="text-slate-400" size={22} />
          </div>
          <div>
            <div className="text-[13.5px] font-bold text-slate-900 dark:text-white">No upcoming approved holidays</div>
            <div className="text-[12px] text-slate-500 dark:text-slate-400">Ask HR to submit new holidays from the Create tab.</div>
          </div>
        </div>
      </div>
    );
  }
  const label = days === 0 ? "Today" : days === 1 ? "Tomorrow" : `in ${days} days`;
  return (
    <div className="pk-card p-5 border-l-4 border-emerald-500 bg-gradient-to-br from-emerald-50/60 to-transparent dark:from-emerald-900/20" data-testid="holidays-next-card">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-500/20 grid place-items-center flex-shrink-0">
          <Sparkles className="text-emerald-600 dark:text-emerald-300" size={26} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300">Next Holiday</div>
          <div className="text-lg font-extrabold heading text-slate-900 dark:text-white mt-0.5" data-testid="holidays-next-name">{h.name}</div>
          <div className="text-[12.5px] text-slate-600 dark:text-slate-300">{fmtDate(h.date)}{h.region ? ` · ${h.region}` : ""}</div>
        </div>
        <div className="text-right">
          <div className="heading text-2xl font-extrabold text-emerald-700 dark:text-emerald-300 mono" data-testid="holidays-next-days">{label}</div>
        </div>
      </div>
    </div>
  );
}

function YearPicker({ value, onChange, testid }) {
  const y = new Date().getFullYear();
  const years = [y - 2, y - 1, y, y + 1];
  return (
    <div className="flex items-center gap-1">
      {years.map((yy) => (
        <button
          key={yy}
          type="button"
          onClick={() => onChange(yy)}
          className={`h-9 px-3 rounded-lg text-[12.5px] font-semibold transition-colors ${
            yy === value
              ? "bg-emerald-600 text-white shadow"
              : "bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
          }`}
          data-testid={`${testid}-${yy}`}
        >
          {yy}
        </button>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Holidays list — Card OR List view
// ────────────────────────────────────────────────────────────────
function HolidaysView({ mode, rows, variant, isMaster, currentUserId, onEditRequest, onDeleteRequest, onApprove, onReject, emptyMsg, testidPrefix }) {
  if (rows.length === 0) {
    return <div className="pk-card p-10 text-center text-slate-400 text-sm" data-testid={`${testidPrefix}-empty`}>{emptyMsg}</div>;
  }
  if (mode === "list") {
    return <HolidaysList rows={rows} variant={variant} isMaster={isMaster} currentUserId={currentUserId} onEditRequest={onEditRequest} onDeleteRequest={onDeleteRequest} onApprove={onApprove} onReject={onReject} testidPrefix={testidPrefix} />;
  }
  return <HolidaysCards rows={rows} variant={variant} isMaster={isMaster} currentUserId={currentUserId} onEditRequest={onEditRequest} onDeleteRequest={onDeleteRequest} onApprove={onApprove} onReject={onReject} testidPrefix={testidPrefix} />;
}

function HolidayActions({ h, variant, isMaster, currentUserId, onEditRequest, onDeleteRequest, onApprove, onReject }) {
  if (variant !== "create") return null;
  const pendingEdit = h.pending_edit;
  const pendingDelete = h.pending_delete;
  const hasPending = !!(pendingEdit || pendingDelete);
  const canApprove = isMaster && (h.status === "pending" || hasPending);
  const canReject = isMaster && (h.status === "pending" || hasPending);
  const isMine = h.created_by_id === currentUserId;
  const canEditReq = h.status === "approved" && !hasPending;
  const canDeleteReq = !hasPending && (h.status === "approved" || isMine || isMaster);
  return (
    <div className="flex items-center flex-wrap gap-1.5">
      {canApprove && (
        <Button size="sm" onClick={(e) => onApprove(h, e)} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] px-2" data-testid={`holidays-approve-${h.id}`}>
          <Check size={12} className="mr-1" /> Approve
        </Button>
      )}
      {canReject && (
        <RejectPopover holiday={h} onReject={onReject} testid={`holidays-reject-${h.id}`} />
      )}
      {canEditReq && (
        <Button size="sm" variant="outline" onClick={() => onEditRequest(h)} className="h-7 text-[11px] px-2" data-testid={`holidays-edit-${h.id}`}>
          <Pencil size={11} className="mr-1" /> Request Edit
        </Button>
      )}
      {canDeleteReq && (
        <Button size="sm" variant="outline" onClick={() => onDeleteRequest(h)} className="h-7 text-[11px] px-2 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`holidays-delete-${h.id}`}>
          <Trash2 size={11} className="mr-1" /> {h.status === "approved" ? "Request Delete" : "Cancel"}
        </Button>
      )}
    </div>
  );
}

function PendingBadge({ h }) {
  if (h.pending_edit) return <Badge className="bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200 text-[10px]">EDIT REQUESTED</Badge>;
  if (h.pending_delete) return <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200 text-[10px]">DELETE REQUESTED</Badge>;
  return null;
}

function HolidaysCards({ rows, variant, isMaster, currentUserId, onEditRequest, onDeleteRequest, onApprove, onReject, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {rows.map((h) => (
        <div key={h.id} className="pk-card p-4 space-y-2 hover:shadow-md transition-shadow" data-testid={`${testidPrefix}-card-${h.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-500/20 grid place-items-center flex-shrink-0">
                <CalendarCheck className="text-emerald-700 dark:text-emerald-300" size={16} />
              </div>
              <div>
                <div className="text-[13.5px] font-bold text-slate-900 dark:text-white leading-tight">{h.name}</div>
                <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mono">{fmtDate(h.date)}</div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {variant === "create" && <Badge className={STATUS_TONES[h.status] || ""}>{h.status}</Badge>}
              <PendingBadge h={h} />
            </div>
          </div>
          {h.region && <div className="text-[11.5px] text-slate-500 dark:text-slate-400">Region: {h.region}</div>}
          {variant === "create" && (
            <div className="text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800 pt-2 space-y-0.5">
              <div className="flex items-center gap-1"><Users size={11} /> Created by <b className="text-slate-700 dark:text-slate-200">{h.created_by_name || "—"}</b></div>
              <div className="flex items-center gap-1"><Clock size={11} /> {fmtDateTime(h.created_at)}</div>
              {h.decided_by_name && h.status !== "pending" && (
                <div className="italic">{h.status === "approved" ? "Approved" : "Rejected"} by {h.decided_by_name} · {fmtDateTime(h.decided_at)}{h.decision_reason ? ` — "${h.decision_reason}"` : ""}</div>
              )}
              {h.pending_edit && (
                <div className="text-sky-700 dark:text-sky-300"><b>Edit</b> requested by {h.pending_edit.requested_by_name} — &ldquo;{h.pending_edit.reason}&rdquo;</div>
              )}
              {h.pending_delete && (
                <div className="text-rose-700 dark:text-rose-300"><b>Delete</b> requested by {h.pending_delete.requested_by_name} — &ldquo;{h.pending_delete.reason}&rdquo;</div>
              )}
            </div>
          )}
          <HolidayActions h={h} variant={variant} isMaster={isMaster} currentUserId={currentUserId} onEditRequest={onEditRequest} onDeleteRequest={onDeleteRequest} onApprove={onApprove} onReject={onReject} />
        </div>
      ))}
    </div>
  );
}

function HolidaysList({ rows, variant, isMaster, currentUserId, onEditRequest, onDeleteRequest, onApprove, onReject, testidPrefix }) {
  return (
    <div className="pk-card overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 bg-slate-50/60 dark:bg-slate-900/40">
          <tr>
            <th className="px-4 py-3 text-left pk-table-header">Date</th>
            <th className="px-4 py-3 text-left pk-table-header">Holiday</th>
            <th className="px-4 py-3 text-left pk-table-header">Region</th>
            {variant === "create" && <th className="px-4 py-3 text-left pk-table-header">Created By</th>}
            {variant === "create" && <th className="px-4 py-3 text-center pk-table-header">Status</th>}
            <th className="px-4 py-3 text-right pk-table-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-900/40" data-testid={`${testidPrefix}-row-${h.id}`}>
              <td className="px-4 py-3 text-[12.5px] mono text-slate-800 dark:text-slate-200" data-label="Date">{fmtDate(h.date)}</td>
              <td className="px-4 py-3" data-label="Holiday">
                <div className="font-semibold text-slate-900 dark:text-white">{h.name}</div>
                {h.pending_edit && <div className="text-[10.5px] italic text-sky-700 dark:text-sky-300">Edit → {h.pending_edit.name} · {h.pending_edit.date}</div>}
              </td>
              <td className="px-4 py-3 text-[12px] text-slate-600 dark:text-slate-300" data-label="Region">{h.region || "—"}</td>
              {variant === "create" && (
                <td className="px-4 py-3 text-[12px] text-slate-600 dark:text-slate-300" data-label="Created By">
                  <div>{h.created_by_name || "—"}</div>
                  <div className="text-[10.5px] text-slate-400">{fmtDateTime(h.created_at)}</div>
                </td>
              )}
              {variant === "create" && (
                <td className="px-4 py-3 text-center" data-label="Status">
                  <Badge className={STATUS_TONES[h.status] || ""}>{h.status}</Badge>
                  <PendingBadge h={h} />
                </td>
              )}
              <td className="px-4 py-3 text-right" data-label="Actions">
                <HolidayActions h={h} variant={variant} isMaster={isMaster} currentUserId={currentUserId} onEditRequest={onEditRequest} onDeleteRequest={onDeleteRequest} onApprove={onApprove} onReject={onReject} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Reject popover (inline reason input)
// ────────────────────────────────────────────────────────────────
function RejectPopover({ holiday, onReject, testid }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="h-7 text-[11px] px-2 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={testid}>
        <XCircle size={12} className="mr-1" /> Reject
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject holiday</DialogTitle>
            <DialogDescription>Reason is optional but recommended so the requester understands.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <div className="text-[13px]"><b>{holiday.name}</b> · {fmtDate(holiday.date)}</div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold">Reason (optional)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason for rejection…" data-testid={`${testid}-reason`} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => { onReject(holiday, reason.trim()); setOpen(false); setReason(""); }} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid={`${testid}-confirm`}>Confirm Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Add Holidays dialog (multi-entry)
// ────────────────────────────────────────────────────────────────
function AddHolidaysDialog({ open, onClose, onSaved, isMaster }) {
  const [entries, setEntries] = useState([{ date: "", name: "", region: "" }]);
  const [saving, setSaving] = useState(false);

  const setEntry = (idx, key, val) => {
    setEntries((es) => es.map((e, i) => (i === idx ? { ...e, [key]: val } : e)));
  };
  const addRow = () => setEntries((es) => [...es, { date: "", name: "", region: "" }]);
  const removeRow = (idx) => setEntries((es) => es.filter((_, i) => i !== idx));

  const submit = async () => {
    const valid = entries.filter((e) => e.date && e.name.trim());
    if (valid.length === 0) { toast.error("Add at least one holiday with date + name."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/holidays/bulk", {
        holidays: valid.map((e) => ({ date: e.date, name: e.name.trim(), region: e.region.trim() })),
      });
      const insertedN = (data.inserted || []).length;
      const skippedN = (data.skipped || []).length;
      const suffix = isMaster ? "auto-approved" : "sent for approval";
      if (insertedN > 0) toast.success(`${insertedN} holiday${insertedN !== 1 ? "s" : ""} ${suffix}${skippedN ? ` · ${skippedN} skipped` : ""}`);
      else toast.error(`No holidays inserted${skippedN ? ` · ${skippedN} skipped (duplicates?)` : ""}`);
      onSaved();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="holidays-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus size={18} /> Add Holidays</DialogTitle>
          <DialogDescription>
            Add one or many holidays at once. {isMaster ? "You'll auto-approve them on submit." : "Master Admin will review each entry before it appears in the View tab."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {entries.map((e, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end pk-card p-3" data-testid={`holidays-add-row-${idx}`}>
              <div className="col-span-4">
                <Label className="text-[10.5px] uppercase tracking-wider font-semibold">Date <span className="text-rose-500">*</span></Label>
                <Input type="date" value={e.date} onChange={(ev) => setEntry(idx, "date", ev.target.value)} className="h-9 mono mt-1" data-testid={`holidays-add-date-${idx}`} />
              </div>
              <div className="col-span-5">
                <Label className="text-[10.5px] uppercase tracking-wider font-semibold">Holiday Name <span className="text-rose-500">*</span></Label>
                <Input value={e.name} onChange={(ev) => setEntry(idx, "name", ev.target.value)} placeholder="e.g. Republic Day" className="h-9 mt-1" data-testid={`holidays-add-name-${idx}`} />
              </div>
              <div className="col-span-2">
                <Label className="text-[10.5px] uppercase tracking-wider font-semibold">Region</Label>
                <Input value={e.region} onChange={(ev) => setEntry(idx, "region", ev.target.value)} placeholder="Optional" className="h-9 mt-1" data-testid={`holidays-add-region-${idx}`} />
              </div>
              <div className="col-span-1 flex justify-end">
                {entries.length > 1 && (
                  <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50" onClick={() => removeRow(idx)} data-testid={`holidays-add-remove-${idx}`}>
                    <X size={16} />
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addRow} className="w-full h-10 border-dashed" data-testid="holidays-add-more">
            <Plus size={14} className="mr-1.5" /> Add another entry
          </Button>
        </div>

        <div className="text-[11.5px] text-slate-500 dark:text-slate-400 flex items-start gap-2 border-t border-slate-100 dark:border-slate-800 pt-3">
          <AlertTriangle size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>{isMaster ? "As Master Admin, submitted entries are auto-approved." : "Entries stay in Pending until Master Admin approves them from this tab."}</div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="holidays-add-submit">
            {saving ? "Submitting…" : isMaster ? "Submit &amp; Auto-Approve" : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────
// Edit Request dialog
// ────────────────────────────────────────────────────────────────
function EditRequestDialog({ open, holiday, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: holiday.date, name: holiday.name, region: holiday.region || "", reason: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if ((form.reason || "").trim().length < 3) { toast.error("Please add a reason (min 3 chars)."); return; }
    if (!form.date || !form.name.trim()) { toast.error("Date and name are required."); return; }
    setSaving(true);
    try {
      await api.post(`/holidays/${holiday.id}/request-edit`, {
        date: form.date, name: form.name.trim(), region: form.region.trim(), reason: form.reason.trim(),
      });
      toast.success("Edit request sent to Master Admin");
      onSaved();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="holidays-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil size={16} /> Request Edit</DialogTitle>
          <DialogDescription>This holiday is approved — changes require Master Admin approval.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold">Date <span className="text-rose-500">*</span></Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-10 mono" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold">Holiday Name <span className="text-rose-500">*</span></Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-10" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold">Region</Label>
            <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="mt-1.5 h-10" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold">Reason for edit <span className="text-rose-500">*</span> <span className="text-slate-400 normal-case">(min 3 chars)</span></Label>
            <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} placeholder="Why is this change needed?" className="mt-1.5" data-testid="holidays-edit-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="holidays-edit-submit">
            {saving ? "Sending…" : "Send Edit Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────
// Delete Request dialog
// ────────────────────────────────────────────────────────────────
function DeleteRequestDialog({ open, holiday, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const isApproved = holiday.status === "approved";

  const submit = async () => {
    if (isApproved && reason.trim().length < 3) { toast.error("Please add a reason (min 3 chars)."); return; }
    setSaving(true);
    try {
      await api.post(`/holidays/${holiday.id}/request-delete`, { reason: reason.trim() });
      toast.success(isApproved ? "Delete request sent to Master Admin" : "Holiday cancelled");
      onSaved();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="holidays-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-600"><Trash2 size={16} /> {isApproved ? "Request Delete" : "Cancel Holiday"}</DialogTitle>
          <DialogDescription>
            {isApproved
              ? "This holiday is approved — deletion requires Master Admin approval."
              : "This entry is not yet approved. You can cancel it directly."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="pk-card p-3 bg-slate-50/60 dark:bg-slate-900/40 text-[13px]">
            <b>{holiday.name}</b> · {fmtDate(holiday.date)}{holiday.region ? ` · ${holiday.region}` : ""}
          </div>
          {isApproved && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Reason for delete <span className="text-rose-500">*</span> <span className="text-slate-400 normal-case">(min 3 chars)</span></Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Why should this holiday be removed?" className="mt-1.5" data-testid="holidays-delete-reason" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="holidays-delete-submit">
            {saving ? "Sending…" : isApproved ? "Send Delete Request" : "Confirm Cancel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
